import { Injectable } from '@angular/core';

export enum SortOrder {
  ASC = 1,
  DESC = -1,
}

export enum DataType {
  String = 'string',
  Number = 'number',
  Object = 'object',
  Date = 'date',
}

@Injectable({
  providedIn: 'root',
})
export class SortService {
  private order: SortOrder;
  private nullException: any;
  /**
   * * Checks for the following date formats.
   * * - YYYY/MM/DD
   * * - YYYY-MM-DD
   * * - MM/DD/YYYY
   * * - MM-DD-YYYY
   */
  private dateReg = /(^\d{4}[\/-]\d{2}[\/-]\d{2})|(^\d{2}[\/-]\d{2}[\/-]\d{4})/;
  // * Checks for value starting with a number, positive or negative.
  private negNumReg = /(^\-?\d+)(.*)/i;
  // * Checks for non-numeric values.
  private nonChar = /[^-\d]+/;
  // * Checks for numeric values.
  private nonDigit = /[^-\D]+/;

  /**
   *
   * @param valueA First value to compare
   * @param valueB Second value to compare
   * @param order Sorting order "SortOrder.ASC" or "SortOrder.DESC"
   * @optional nullException value exception to use when value is null
   * @returns number
   */
  singleSort(valueA: string | number, valueB: string | number, order: SortOrder, nullException?: any): number {
    this.order = order || SortOrder.ASC;
    this.nullException = nullException;
    return this.doSort(valueA, valueB);
  }

  private doSort(valueA: any, valueB: any): number {
    // * Convert data type to facilitate sorting.
    [valueA, valueB] = this.dataConversion(valueA, valueB);
    return (
      (valueA - valueB ||
        `${valueA}`.localeCompare(valueB as string, undefined, { numeric: true, sensitivity: 'base' })) * this.order
    );
  }

  // * Get numeric value from string an return it as number -> '23B'-> 23
  private getNumberStr(value: string): number {
    return Number(value.replace(this.nonChar, ''));
  }

  // * Return non-numeric value from string. -> '23AB'-> 'AB'
  private getCharStr = (value: string) => {
    return value.replace(this.nonDigit, '');
  };

  private isNullish(value: string | number): string | number {
    const exception = this.nullException ?? Number.MIN_SAFE_INTEGER;
    return value ?? exception;
  }

  private getDateTime(value: string): number | string {
    // * If date is invalid, return original value...
    return new Date(value).getTime() || value;
  }

  private isDate(value: any): number | string {
    if (this.dateReg.test(value)) {
      return this.getDateTime(value);
    }
    return value;
  }

  /**
   * * Convert data to facilitate sorting.
   * * ex:
   * *  - string date to number
   * *  - string number to number
   * *  - string number to number
   */
  private dataConversion(valueA: any, valueB: any): [valueA: string | number, valueB: string | number] {
    valueA = this.isDate(valueA) || this.isNullish(valueA);
    valueB = this.isDate(valueB) || this.isNullish(valueB);
    // * Insolate negative numeric from string if is present on the data...
    if (this.negNumReg.test(valueA) && this.negNumReg.test(valueB)) {
      const numA = this.getNumberStr(`${valueA}`);
      const numB = this.getNumberStr(`${valueB}`);
      /**
       * * If numeric negative values are the same compare data by the string content instead.
       * * ex: (valueA: '-22A', valueB: '-22C') -> (valueA: 'A', valueB: 'C')
       */
      valueA = numA === numB ? this.getCharStr(`${valueA}`) : numA;
      valueB = numA === numB ? this.getCharStr(`${valueB}`) : numB;
    }

    return [valueA, valueB];
  }
}
